import React, { useState, useEffect, useRef } from 'react';
import { ImageOff, Upload, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export default function MissingImages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatus] = useState('In Stock');
  const [selections, setSelections] = useState({}); // { [code]: File }
  const [previews, setPreviews] = useState({});  // { [code]: objectURL }
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // { uploaded, failed }
  const fileInputRefs = useRef({});

  const fetchItems = async () => {
    setLoading(true);
    setResults(null);
    try {
      const qs = statusFilter !== 'all' ? `?status=${encodeURIComponent(statusFilter)}` : '?status=all';
      const res = await fetch(`${API_BASE_URL}/reports/missing-images${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setItems(data.data);
      setSelections({});
      setPreviews({});
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => { Object.values(previews).forEach(URL.revokeObjectURL); };
  }, [previews]);

  const handleFileChange = (code, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviews(prev => ({ ...prev, [code]: url }));
    setSelections(prev => ({ ...prev, [code]: file }));
  };

  const clearSelection = (code) => {
    if (previews[code]) URL.revokeObjectURL(previews[code]);
    setPreviews(prev => { const n = { ...prev }; delete n[code]; return n; });
    setSelections(prev => { const n = { ...prev }; delete n[code]; return n; });
    if (fileInputRefs.current[code]) fileInputRefs.current[code].value = '';
  };

  const handleSubmit = async () => {
    const entries = Object.entries(selections);
    if (entries.length === 0) return;

    setSubmitting(true);
    setResults(null);
    const uploaded = [], failed = [];

    await Promise.all(entries.map(async ([code, file]) => {
      try {
        const form = new FormData();
        form.append('image', file);
        form.append('itemCode', code);
        const res = await fetch(`${API_BASE_URL}/inventory/upload-image`, {
          method: 'POST',
          credentials: 'include',
          body: form,
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Upload failed');
        uploaded.push(code);
      } catch (e) {
        failed.push({ code, reason: e.message });
      }
    }));

    setResults({ uploaded, failed });
    setSubmitting(false);

    // Remove successfully uploaded items from the list
    if (uploaded.length > 0) {
      setItems(prev => prev.filter(item => !uploaded.includes(item.code)));
      uploaded.forEach(code => clearSelection(code));
    }
  };

  const selectedCount = Object.keys(selections).length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.UTILITIES.MISSING_IMAGES} pageName="Missing Images" />
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white pb-3 pt-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ImageOff size={22} className="text-slate-500" />
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Items Missing Photos</h1>
              {!loading && (
                <p className="text-sm text-slate-500">
                  {items.length} item{items.length !== 1 ? 's' : ''} have no image
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={e => setStatus(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="In Stock">In Stock</option>
              <option value="Sold">Sold</option>
              <option value="Return">Return</option>
              <option value="all">All Statuses</option>
            </select>

            <button
              onClick={fetchItems}
              disabled={loading}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>

            <button
              onClick={handleSubmit}
              disabled={selectedCount === 0 || submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <><RefreshCw size={14} className="animate-spin" /> Uploading…</>
              ) : (
                <><Upload size={14} /> Submit {selectedCount > 0 ? `(${selectedCount})` : ''}</>
              )}
            </button>
          </div>
        </div>

        {/* Result banner — inside sticky so it stays visible too */}
        {results && (
          <div className={`mt-3 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${results.failed.length === 0 ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
            <CheckCircle size={16} className="flex-shrink-0" />
            <span>
              {results.uploaded.length} uploaded successfully.
              {results.failed.length > 0 && ` ${results.failed.length} failed: ${results.failed.map(f => f.code).join(', ')}`}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <RefreshCw size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400">
          <CheckCircle size={40} className="mb-3 text-green-400" />
          <p className="font-medium">All items have photos</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 text-left">Item Code</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-right">Gross Wt (g)</th>
                  <th className="px-4 py-3 text-center">Purity</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center w-52">Select Photo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map(item => (
                  <tr key={item.code} className={`hover:bg-slate-50 transition-colors ${selections[item.code] ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3 font-mono font-medium text-slate-800">{item.code}</td>
                    <td className="px-4 py-3 text-slate-700">{item.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{item.category_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{item.vendor_code || '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{item.gross_weight ? parseFloat(item.gross_weight).toFixed(3) : '—'}</td>
                    <td className="px-4 py-3 text-center text-slate-700">{item.gold_purity ? `${item.gold_purity}K` : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.status === 'In Stock' ? 'bg-green-100 text-green-700' :
                          item.status === 'Sold' ? 'bg-slate-100 text-slate-600' :
                            'bg-yellow-100 text-yellow-700'
                        }`}>{item.status}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {selections[item.code] ? (
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={previews[item.code]}
                            alt="preview"
                            className="h-9 w-9 rounded object-cover border border-slate-200"
                          />
                          <span className="text-xs text-slate-500 max-w-[80px] truncate">
                            {selections[item.code].name}
                          </span>
                          <button
                            onClick={() => clearSelection(item.code)}
                            className="text-slate-400 hover:text-red-500"
                            title="Remove"
                          >
                            <XCircle size={15} />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 hover:border-blue-400 hover:text-blue-600 transition-colors">
                          <Upload size={12} />
                          Choose
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => { fileInputRefs.current[item.code] = el; }}
                            onChange={e => handleFileChange(item.code, e.target.files[0])}
                          />
                        </label>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
